import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ProcessMenu } from '../components/ProcessMenu.js';
import {
  PROCESS_MENU_OPTIONS,
  makeProcessMenuHandler,
} from './process-menu-shared.js';
import { api } from '../api/client.js';
import { useCustomer } from '../customer-context.js';
import { EmailTemplateMenu } from '../email-templates/EmailTemplateMenu.js';
import { TRANSACTION_INFORMATION_TEMPLATES } from '../email-templates/templates.js';
import { useContentMap } from '../content.js';

/**
 * SCRN 47 — Transaction Information (CCF-86).
 *
 * Faithful port of the customer's Figma "Transaction information" design:
 * read-only Financial / Transaction / Audit detail boxes, plus the SEPA
 * Originating/Destination block which is shown ONLY for SEPA transactions
 * (the Figma's Direct-Debit Originating/Destination/Untitled boxes map to the
 * contract's `Sepa` block; per CCF-86 these are SEPA/DD-only).
 *
 * The transaction id arrives as the `?id=` query param from the listing
 * (SCRN 46). Detail is fetched from the Teller-ID-gated BFF via the generated
 * typed client (`POST /api/accounts/transaction-information`). The shared agent
 * banner lives in the app shell, so this screen renders only the `<main>`.
 */

/** Open contract shape from `/accounts/transaction-information`. */
interface SepaBlock {
  OriginatingIban?: string;
  OriginatingName?: string;
  OriginatingBic?: string;
  OriginatingId?: string;
  DestinationIban?: string;
  DestinationName?: string;
  DestinationBic?: string;
  DestinationId?: string;
  RemittanceInfo?: string;
  /**
   * §5 Originating "Reference" — the gateway's `ReferenceofTransaction` (#125).
   * Replaces the former `EndToEndId` carrier.
   */
  Reference?: string;
  UniqueReference?: string;
  SettlementDate?: string;
  ResponseCode?: string;
  /** §7 Settlement amount — the gateway's `TransactionAmount` (#125). */
  TransactionAmount?: number;
}

interface TransactionInfo {
  Found?: boolean;
  AccountNumber?: string;
  TransactionId?: string;
  /** 6-digit transaction reference shown in the header/audit trail (CCF-86). */
  Ref?: string;
  Date?: string;
  Description?: string;
  Amount?: number;
  DrCr?: 'DR' | 'CR';
  RunningBalance?: number;
  Status?: string;
  Type?: string;
  Merchant?: string;
  /**
   * Last 4 digits of the card the transaction was made with (CCF-86 / #355).
   * The `transaction/information` gateway op returns the full PAN, but the BFF
   * masks it to its last-4 before it reaches the client (full-PAN display is
   * Dashboard-only, Dylan #97/#85), so this is the only card value the screen
   * ever sees. Rendered as `•••• <last4>` in the header "Card number" field;
   * absent for a non-card transaction.
   */
  CardLast4?: string;
  /** Branch sort code / NSC for the audit trail (CCF-86). */
  Nsc?: string;
  // --- CCF-86 Financial details ---
  TransactionCode?: string;
  CashAmount?: number;
  ChequeAmount?: number;
  NotesAmount?: number;
  CashBack?: number;
  CoinAmount?: number;
  LNKRefId?: string;
  Voucher?: string;
  // --- CCF-86 Transaction details ---
  Payer?: string;
  Payee?: string;
  ReasonCode?: string;
  FinancialUserType?: string;
  Item?: number;
  RemitData?: string;
  // --- CCF-86 Audit trail ---
  Teller?: string;
  Terminal?: string;
  Batch?: string;
  Jobname?: string;
  PostingDate?: string;
  /** Transaction time (CCF-86 audit trail) — threaded from listing InputTime. */
  Time?: string;
  /** Authorising user (CCF-86 "User POOL CASH 3 authorised by"); see Source. */
  Source?: string;
  SequenceNumber?: string;
  Sepa?: SepaBlock;
}

/** Renders a value that may legitimately be empty/zero, else the em-dash. */
function orDash(value?: string | number): React.ReactNode {
  if (value === undefined || value === null || value === '') return '—';
  return String(value);
}

/**
 * Formats a transaction amount in the account's currency (issue #415). The
 * currency is threaded from the listing (SCRN 46) via the `currency` query
 * param so a foreign-currency account's amounts render the correct symbol
 * (USD → US$, GBP → £, …). Defaults to EUR and falls back to a code-prefixed
 * string for an unrecognised ISO code.
 */
function formatCurrency(amount?: number, currency = 'EUR'): string {
  if (typeof amount !== 'number') return '—';
  const code = currency || 'EUR';
  try {
    return new Intl.NumberFormat('en-IE', {
      style: 'currency',
      currency: code,
    }).format(amount);
  } catch {
    return `${code} ${amount.toFixed(2)}`;
  }
}

function formatDate(iso?: string): string {
  if (!iso) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
}

/**
 * Format the listing row's gateway `InputTime` (an HHMMSShh string — hours,
 * minutes, seconds, hundredths) into a human HH:MM:SS for the CCF-86 audit
 * trail's "Time" row (#125). Returns '' for an unusable/short value so the row
 * shows the em-dash rather than a malformed time.
 */
function formatInputTime(raw?: string): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  if (digits.length < 6) return '';
  return `${digits.slice(0, 2)}:${digits.slice(2, 4)}:${digits.slice(4, 6)}`;
}

/** A single read-only label/value pair, faithful to the export's detail rows. */
function DetailRow({
  label,
  value,
}: {
  label: string;
  value?: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="flex items-start">
      <span className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] min-w-[160px]">
        {label}
      </span>
      <span className="text-[14px] font-['Arial',sans-serif] text-[#1d252c]">
        {value ?? '—'}
      </span>
    </div>
  );
}

/** A bordered read-only detail box with a heading. */
function DetailBox({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="mb-8 border-2 border-[#d1d5db] p-8 bg-white max-w-[1200px]">
      <h2 className="text-[20px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-6">
        {title}
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-2 gap-y-4">
        {children}
      </div>
    </div>
  );
}

export function TransactionInformationScreen(): React.JSX.Element {
  const { customer } = useCustomer();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // #637 inc 16 — the resolved content-catalogue map, threaded into the Proof of
  // Payment email template so its editable wording (title/subject, warning
  // lines, field labels and the screenshot instruction) reflects any admin
  // reword; absent-key defaults keep the output unchanged.
  const contentMap = useContentMap();
  const transactionId = searchParams.get('id') ?? '';
  const accountNumber = searchParams.get('account') ?? '';
  // The real PTSB gateway resolves a transaction by the composite key the
  // listing row carries (InputDate/InputTime/InputTime2/SeqNo), NOT an opaque
  // id — omitting it returned a 400 and an empty screen (issue #125). The
  // listing (SCRN 46) threads these through the query string.
  const inputDate = searchParams.get('inputDate') ?? '';
  const inputTime = searchParams.get('inputTime') ?? '';
  const inputTime2 = searchParams.get('inputTime2') ?? '';
  const sequenceNumber = searchParams.get('sequenceNumber') ?? '';
  // SEPA indicator (#125): a non-empty `SepaKeys` from the listing row marks a
  // SEPA/Direct-Debit transaction. Forwarded to the BFF so it makes the SECOND
  // call (`transaction/information/sepa`) that fetches the §5/6/7 boxes; for a
  // non-SEPA transaction it is absent and those sections do not render.
  const sepaKeys = searchParams.get('sepaKeys') ?? '';
  // Listing-only DISPLAY fields the real `transaction/information` op does not
  // return (Ref / Dr-Cr / Type / Date); the listing (SCRN 46) threads them so
  // the detail screen can still show them as fallbacks (issue #125).
  const refParam = searchParams.get('ref') ?? '';
  const drCrParam = searchParams.get('drCr') ?? '';
  const typeParam = searchParams.get('type') ?? '';
  const dateParam = searchParams.get('date') ?? '';
  // Account currency (issue #415): the real `transaction/information` op does
  // NOT return a currency, so the listing (SCRN 46) threads the drilled-down
  // account's ISO currency code through the query string. Amounts render in it
  // (USD → US$, …); absent → EUR.
  const currencyParam = searchParams.get('currency') ?? 'EUR';

  // Shared process-menu navigation handler (issue #45) — wires each option to
  // its existing, RBAC-gated route.
  const onProcessSelect = makeProcessMenuHandler(navigate, accountNumber);

  const customerNumber = customer.cif ?? '';
  const tellerId = customer.tellerId ?? '';

  const [info, setInfo] = useState<TransactionInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      if (!customerNumber || !/^\d{4}$/.test(tellerId)) {
        setError('No verified customer in context. Complete ID&V first.');
        return;
      }
      if (!transactionId || !accountNumber) {
        setError('No transaction selected.');
        return;
      }
      setLoading(true);
      setError('');
      try {
        const {
          data,
          error: apiError,
          response,
        } = await api.POST('/accounts/transaction-information', {
          params: { header: { 'X-Teller-Id': tellerId } },
          body: {
            customerNumber,
            accountNumber,
            transactionId,
            // Composite key the real gateway needs to resolve the row (#125).
            ...(inputDate ? { inputDate } : {}),
            ...(inputTime ? { inputTime } : {}),
            ...(inputTime2 ? { inputTime2 } : {}),
            ...(sequenceNumber ? { sequenceNumber } : {}),
            // SEPA indicator → BFF makes the second SEPA-detail call (#125).
            ...(sepaKeys ? { sepaKeys } : {}),
          },
        });
        if (cancelled) return;
        if (apiError || !data) {
          setError(
            response.status === 403
              ? 'Forbidden: check your Teller ID and permissions.'
              : `Could not load transaction (${response.status}).`,
          );
          return;
        }
        const detail = data as unknown as TransactionInfo;
        if (detail.Found === false) {
          setError('Transaction not found.');
          return;
        }
        // Merge the listing-threaded display fields (Ref / Dr-Cr / Type / Date)
        // UNDER the gateway detail, so the gateway-returned values win where
        // present and the listing fills the fields the detail op omits (#125).
        const fallback: Partial<TransactionInfo> = {
          ...(refParam ? { Ref: refParam } : {}),
          ...(drCrParam === 'DR' || drCrParam === 'CR'
            ? { DrCr: drCrParam }
            : {}),
          ...(typeParam ? { Type: typeParam } : {}),
          ...(dateParam ? { Date: dateParam } : {}),
          // Sequence number (#384): the real `transaction/information` op does
          // NOT return it — it is a LISTING-only field carried as the composite
          // key. The BFF now threads it onto the detail response, but keep the
          // listing-row value as a client fallback too so the "Sequence" audit
          // row is populated even when a variant omits it (matches the Ref/Date
          // threading above). The gateway/threaded value still wins via spread.
          ...(sequenceNumber ? { SequenceNumber: sequenceNumber } : {}),
          // The real `transaction/information` op returns no time; the audit
          // trail's "Time" is threaded from the listing row's InputTime
          // (HHMMSShh), formatted to HH:MM:SS (#125).
          ...(formatInputTime(inputTime)
            ? { Time: formatInputTime(inputTime) }
            : {}),
        };
        setInfo({ ...fallback, ...detail });
      } catch {
        if (!cancelled) setError('Request failed.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    customerNumber,
    tellerId,
    accountNumber,
    transactionId,
    inputDate,
    inputTime,
    inputTime2,
    sequenceNumber,
    sepaKeys,
  ]);

  const sepa = info?.Sepa;

  return (
    <main
      className="max-w-[1800px] mx-auto px-12 py-12"
      aria-labelledby="transaction-info-heading"
    >
      <div className="mb-8 flex items-center justify-between gap-4 flex-wrap">
        <a
          href="/transactions"
          onClick={(e) => {
            e.preventDefault();
            // Signal the listing to restore the prior query/results (issue #55).
            // Only this dedicated back link sets `restore`; entering the
            // transactions screen any other way starts it clean.
            navigate('/transactions', { state: { restore: true } });
          }}
          className="inline-flex items-center gap-2 text-[16px] font-['Arial',sans-serif] text-[#FF703E] underline hover:text-[#e55a2a] focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 rounded"
        >
          &lt; Back to transaction listing
        </a>

        {/* Email-templates dropdown (#795, @dylan219): the drill-down offers
            the SAME templates as the Transactions search page (SCRN-46) and
            presents them the same way — a top-right "Email templates" dropdown
            (the shared {@link EmailTemplateMenu}) rather than the former bottom
            button row. Per @dylan219's latest note it is positioned like the
            rest of the servicing screens: in the back-link header row ABOVE
            the process menu (Bill Payments #442 / Standing Orders #705
            pattern), not beside the ProcessMenu in the heading row. Templates
            are permission-free (#710) and prefilled best-effort from the
            loaded transaction detail; the generated `.eml` filename is keyed
            on the subject, never the CIF (#738). Rendered only once the detail
            has loaded, so the per-transaction prefill (destination name,
            posting date, reference, amount) is available. */}
        {info && (
          <EmailTemplateMenu
            templates={TRANSACTION_INFORMATION_TEMPLATES}
            context={{
              customerName: customer.name,
              cif: customer.cif,
              accountNumber: info.AccountNumber ?? accountNumber,
              destinationName: sepa?.DestinationName,
              // Blank (not the on-screen "—" placeholder) when a value is
              // absent, so the email form field stays empty for the agent.
              postingDate:
                (info.PostingDate ?? info.Date)
                  ? formatDate(info.PostingDate ?? info.Date)
                  : '',
              transactionReference: info.Ref,
              transactionAmount:
                typeof info.Amount === 'number'
                  ? formatCurrency(info.Amount, currencyParam)
                  : '',
              content: contentMap,
            }}
          />
        )}
      </div>

      <div className="flex items-center justify-between gap-8 mb-8 flex-wrap">
        <h1
          id="transaction-info-heading"
          className="text-[28px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-6"
        >
          Transaction information
        </h1>
        {/* Process-menu navigation (issue #45): jump to a related process.
            Pure navigation chooser — each route stays independently RBAC-gated
            (deny-by-default). */}
        <ProcessMenu
          options={PROCESS_MENU_OPTIONS}
          onSelect={onProcessSelect}
        />
      </div>

      {error && (
        <div
          role="alert"
          className="mb-8 p-4 bg-white border border-[#dc2626] font-['Arial',sans-serif] text-[14px] text-[#1d252c]"
        >
          {error}
        </div>
      )}

      {loading && (
        <p
          role="status"
          className="font-['Arial',sans-serif] text-[16px] text-[#1d252c]"
        >
          Loading transaction…
        </p>
      )}

      {info && (
        <>
          {/* Grey header box — account number + the card number the
              transaction was made with (CCF-86 grey box). Per #355 the former
              "Reference" field is now "Card number", populated from the card
              number returned by the `transaction/information` gateway op. The
              gateway returns the full PAN but the BFF masks it to its last-4
              before it reaches the client (full-PAN display is Dashboard-only,
              Dylan #97/#85), so the header renders `•••• <last4>`, and the
              em-dash for a non-card transaction. */}
          <div className="mb-8 border-2 border-[#e5e7eb] bg-[#f9fafb] p-6 inline-block">
            <div className="flex gap-x-16 flex-wrap items-center">
              <div className="flex items-center gap-x-3">
                <span className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c]">
                  Account number:
                </span>
                <span className="text-[16px] font-['Arial',sans-serif] text-[#1d252c]">
                  {info.AccountNumber ?? '—'}
                </span>
              </div>
              <div
                className="w-[1px] h-[30px] bg-[#d1d5db]"
                aria-hidden="true"
              ></div>
              <div className="flex items-center gap-x-3">
                <span className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c]">
                  Card number:
                </span>
                <span
                  className="text-[16px] font-['Arial',sans-serif] text-[#1d252c]"
                  aria-label="Card number"
                >
                  {info.CardLast4 ? `•••• ${info.CardLast4}` : '—'}
                </span>
              </div>
            </div>
          </div>

          {/* Financial details (CCF-86 §2). */}
          <DetailBox title="Financial details">
            <DetailRow
              label="Amount:"
              value={formatCurrency(info.Amount, currencyParam)}
            />
            <DetailRow label="Cash amount:" value={orDash(info.CashAmount)} />
            <DetailRow
              label="Cheque amount:"
              value={orDash(info.ChequeAmount)}
            />
            <DetailRow label="Notes amount:" value={orDash(info.NotesAmount)} />
            <DetailRow label="Cash back:" value={orDash(info.CashBack)} />
            <DetailRow label="Coin amount:" value={orDash(info.CoinAmount)} />
            <DetailRow
              label="Transaction code:"
              value={orDash(info.TransactionCode ?? info.Type)}
            />
            <DetailRow
              label="Balance:"
              value={formatCurrency(info.RunningBalance, currencyParam)}
            />
            <DetailRow label="LNK ref ID:" value={orDash(info.LNKRefId)} />
            <DetailRow label="Voucher:" value={orDash(info.Voucher)} />
            <DetailRow
              label="Dr/Cr:"
              value={info.DrCr === 'CR' ? 'Credit' : 'Debit'}
            />
            <DetailRow label="Date:" value={formatDate(info.Date)} />
          </DetailBox>

          {/* Transaction details (CCF-86 §3). */}
          <DetailBox title="Transaction details">
            <DetailRow label="Payer:" value={orDash(info.Payer)} />
            <DetailRow label="Payee:" value={orDash(info.Payee)} />
            <DetailRow label="Reason code:" value={orDash(info.ReasonCode)} />
            <DetailRow
              label="Financial user type:"
              value={orDash(info.FinancialUserType)}
            />
            <DetailRow label="Item:" value={orDash(info.Item)} />
            <DetailRow
              label="Remit data:"
              value={orDash(info.RemitData ?? info.Description)}
            />
          </DetailBox>

          {/* Audit trail (CCF-86 §4). Field set + order match the figma /
              CCF-86 exactly: Branch (NSC), Posting date, Term, Jobname, Time,
              User POOL CASH 3 authorised by, Teller, Batch, Date, Sequence.
              Branch is the NSC (branch sort code), NOT the account number; Time
              is threaded from the listing InputTime; Date/Sequence come from the
              listing row. (The former non-spec Source/Reference/Status rows have
              been removed — #125.) */}
          <DetailBox title="Audit trail">
            <DetailRow label="Branch (NSC):" value={orDash(info.Nsc)} />
            <DetailRow
              label="Posting date:"
              value={formatDate(info.PostingDate ?? info.Date)}
            />
            <DetailRow label="Term:" value={orDash(info.Terminal)} />
            <DetailRow label="Jobname:" value={orDash(info.Jobname)} />
            <DetailRow label="Time:" value={orDash(info.Time)} />
            {/* "User POOL CASH 3 authorised by" has no dedicated gateway field
                in the `transaction/information` response; best-effort = the
                audit `Source` (the originating channel/user), em-dash when
                unsourceable (#125; recorded in PTSB_DEFECTS.md). */}
            <DetailRow
              label="User POOL CASH 3 authorised by:"
              value={orDash(info.Source)}
            />
            <DetailRow label="Teller:" value={orDash(info.Teller)} />
            <DetailRow label="Batch:" value={orDash(info.Batch)} />
            <DetailRow label="Date:" value={formatDate(info.Date)} />
            {/* Sequence (#384): the sequence number, threaded from the listing
                row (`transactions/recent`/`SeqNo`) via the BFF/fallback — the
                real `transaction/information` op does not return it. Renders a
                dash when genuinely absent; do NOT fall back to `Ref` (a
                different field — that previously showed the wrong value). */}
            <DetailRow label="Sequence:" value={orDash(info.SequenceNumber)} />
          </DetailBox>

          {/* SEPA Originating / Destination block — gated on the presence of
              the `Sepa` detail, which the gateway returns only for the
              DD/SEPA case (CCF-86 §5/6/7, Direct-Debit-only sections). The
              `/transaction/information/sepa` API is called only for SEPA/DD
              transactions; non-SEPA transactions omit this block entirely. */}
          {sepa && (
            <section aria-label="SEPA details">
              {/* Originating Details (CCF-86 §5, Direct Debit). */}
              <div className="border-2 border-[#d1d5db] bg-white max-w-[1200px] p-6 mb-6">
                <h2 className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-4">
                  Originating Details
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
                  <DetailRow
                    label="Name:"
                    value={orDash(sepa.OriginatingName)}
                  />
                  <DetailRow label="BIC:" value={orDash(sepa.OriginatingBic)} />
                  <DetailRow
                    label="IBAN:"
                    value={orDash(sepa.OriginatingIban)}
                  />
                  <DetailRow label="ID:" value={orDash(sepa.OriginatingId)} />
                  <DetailRow
                    label="Reference:"
                    value={orDash(sepa.Reference)}
                  />
                </div>
              </div>

              {/* Destination Account Details (CCF-86 §6, Direct Debit). */}
              <div className="border-2 border-[#d1d5db] bg-white max-w-[1200px] p-6 mb-6">
                <h2 className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-4">
                  Destination Account Details
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
                  <DetailRow
                    label="Beneficiary Name:"
                    value={orDash(sepa.DestinationName)}
                  />
                  <DetailRow label="BIC:" value={orDash(sepa.DestinationBic)} />
                  <DetailRow
                    label="Beneficiary ID:"
                    value={orDash(sepa.DestinationId)}
                  />
                  <DetailRow
                    label="IBAN:"
                    value={orDash(sepa.DestinationIban)}
                  />
                </div>
              </div>

              {/* Untitled box (CCF-86 §7, Direct Debit). */}
              <div className="border-2 border-[#d1d5db] bg-white max-w-[1200px] p-6 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 mb-4">
                  <DetailRow
                    label="Transaction Amount:"
                    value={formatCurrency(
                      sepa.TransactionAmount ?? info.Amount,
                      currencyParam,
                    )}
                  />
                  <DetailRow
                    label="Unique Reference:"
                    value={orDash(sepa.UniqueReference ?? sepa.Reference)}
                  />
                  <DetailRow
                    label="Settlement Date:"
                    value={
                      sepa.SettlementDate
                        ? formatDate(sepa.SettlementDate)
                        : formatDate(info.Date)
                    }
                  />
                  <DetailRow
                    label="Response code:"
                    value={orDash(sepa.ResponseCode)}
                  />
                </div>
                <div className="mt-4">
                  <h3 className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-2">
                    Remittance Information
                  </h3>
                  <div className="border-2 border-[#d1d5db] bg-white p-4 min-h-[60px] max-w-[400px]">
                    <p className="text-[14px] font-['Arial',sans-serif] text-[#1d252c] m-0">
                      {sepa.RemittanceInfo ?? ''}
                    </p>
                  </div>
                </div>
              </div>
            </section>
          )}
        </>
      )}
    </main>
  );
}
